import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAsset, getChart, addWatchlist } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { SignalBadge } from "../components/SignalBadge";
import { TradingChart } from "../components/TradingChart";
import AIAnalystPanel from "../components/AIAnalystPanel";
import { ProbabilityWidget } from "../components/ProbabilityWidget";
import { Star, TrendingUp, TrendingDown, CandlestickChart, LineChart as LineChartIcon, AreaChart as AreaChartIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// All timeframes pull the FULL history available from yfinance for the symbol.
// AAPL → dal 1980, BTC → dal 2014, ENI.MI → dal 1998, ecc.
const TIMEFRAMES = [
    { id: "daily",     label: "GIORNI",   interval: "1d",  period: "max" },
    { id: "weekly",    label: "SETTIMANE", interval: "1wk", period: "max" },
    { id: "monthly",   label: "MESI",     interval: "1mo", period: "max" },
    { id: "quarterly", label: "TRIMESTRI", interval: "3mo", period: "max" },
];

const CHART_TYPES = [
    { id: "line", label: "LINEA", icon: LineChartIcon },
    { id: "candle", label: "CANDELE", icon: CandlestickChart },
    { id: "area", label: "AREA", icon: AreaChartIcon },
];

const CHART_TYPE_LABELS = {
    candle: "OHLC CANDLESTICK",
    area: "PRICE AREA",
    line: "PRICE LINE",
};

export default function AssetDetail() {
    const { symbol } = useParams();
    const [asset, setAsset] = useState(null);
    const [chart, setChart] = useState(null);
    const [timeframe, setTimeframe] = useState("daily");
    const [chartType, setChartType] = useState("candle");
    const [loading, setLoading] = useState(true);
    // S/R levels produced by AI Trading Analyst — passed down to TradingChart
    // so they render as dashed price lines on the candles.
    const [analystLevels, setAnalystLevels] = useState(null);
    const { format } = useCurrency();

    // Stable callback — onLevels is a dependency of AIAnalystPanel's useEffect
    // and we don't want the panel to re-fetch every render of AssetDetail.
    const handleAnalystLevels = useCallback((lvls) => setAnalystLevels(lvls), []);

    useEffect(() => {
        setLoading(true);
        const tf = TIMEFRAMES.find(t => t.id === timeframe);
        Promise.all([getAsset(symbol), getChart(symbol, tf.period, tf.interval)])
            .then(([a, c]) => { setAsset(a); setChart(c); })
            .catch((e) => toast.error("Errore caricamento asset: " + (e?.response?.data?.detail || e.message)))
            .finally(() => setLoading(false));
    }, [symbol, timeframe]);

    const handleAdd = async () => {
        try {
            await addWatchlist({ symbol, name: asset?.name });
            toast.success(`${symbol} aggiunto alla watchlist`);
        } catch (e) {
            toast.error("Errore: " + e.message);
        }
    };

    if (loading || !asset) {
        return <div className="px-4 py-20 text-center text-mute">Caricamento {symbol}…</div>;
    }

    const ind = asset.indicators;
    const sig = asset.signal;
    const up = (asset.change_pct || 0) >= 0;

    return (
        <div data-testid="asset-detail-page" className="px-4 py-6 max-w-[1600px] mx-auto fade-up">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-[10px] tracking-[0.2em] text-mute mb-1">ASSET / {symbol}</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold flex items-center gap-3 flex-wrap">
                        <span style={{ color: "var(--brand-blue)" }}>{asset.symbol}</span>
                        <span className="text-dim text-2xl font-normal">{asset.name}</span>
                    </h1>
                    <div className="flex items-center gap-4 mt-3">
                        <span className="text-3xl font-bold tnum">{format(asset.price, { decimals: 4 })}</span>
                        <span className={`text-lg tnum flex items-center gap-1 ${up ? "text-green-up" : "text-red-down"}`}>
                            {up ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                            {up ? "+" : ""}{format(asset.change_abs, { decimals: 4 })} ({asset.change_pct?.toFixed(2)}%)
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <SignalBadge action={sig.action} confidence={sig.confidence} size="lg" />
                    <button
                        data-testid="add-watchlist-btn"
                        onClick={handleAdd}
                        className="border border-strong px-3 py-2 text-xs font-semibold tracking-wider hover:border-amber hover:text-amber flex items-center gap-2"
                    >
                        <Star size={14} />
                        WATCHLIST
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Chart */}
                <div className="lg:col-span-2 border border-soft bg-elev p-4">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="text-xs text-mute tracking-wider">
                            {CHART_TYPE_LABELS[chartType]} · {TIMEFRAMES.find(t => t.id === timeframe)?.label}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div data-testid="chart-type-toggle" className="flex gap-1 border border-soft p-0.5">
                                {CHART_TYPES.map(t => {
                                    const Icon = t.icon;
                                    const isActive = chartType === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            data-testid={`chart-${t.id}`}
                                            onClick={() => setChartType(t.id)}
                                            className={`px-2 py-1 text-[10px] font-semibold flex items-center gap-1 ${isActive ? "text-amber" : "text-dim hover:text-foreground"}`}
                                            style={isActive ? { background: "rgba(212,175,55,0.12)", color: "var(--amber)" } : undefined}
                                            title={t.label}
                                        >
                                            <Icon size={12} />
                                            <span className="hidden sm:inline">{t.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex gap-1">
                                {TIMEFRAMES.map(t => (
                                    <button
                                        key={t.id}
                                        data-testid={`timeframe-${t.id}`}
                                        onClick={() => setTimeframe(t.id)}
                                        className={`px-2 py-1 text-[10px] font-semibold ${timeframe === t.id ? "text-amber border border-amber" : "text-dim border border-transparent hover:text-foreground"}`}
                                        style={timeframe === t.id ? { borderColor: "var(--amber)", color: "var(--amber)" } : undefined}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <TradingChart
                        candles={chart?.candles || []}
                        chartType={chartType}
                        height={400}
                        fibonacci={chart?.fibonacci || null}
                        analystLevels={analystLevels}
                    />
                    <div className="text-[10px] text-mute mt-2 tracking-wide">
                        Zoom: rotella mouse o pinch su mobile · Trascina per scorrere · Doppio click sull'asse per reset
                    </div>
                </div>

                {/* Probability + Signal + AI Analyst */}
                <div className="flex flex-col gap-4">
                    <AIAnalystPanel symbol={symbol} onLevels={handleAnalystLevels} currentPrice={asset.price} />
                    <ProbabilityWidget signal={sig} />
                    <div data-testid="signal-card" className="border border-soft bg-elev p-4">
                        <div className="text-xs text-mute tracking-wider mb-3">MOTIVAZIONI</div>
                        <div className="space-y-2">
                            {sig.reasons.map((r) => (
                                <div key={r} className="flex gap-2 text-xs">
                                    <span className="text-amber">▸</span>
                                    <span className="text-dim">{r}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Indicators grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <IndicatorBox label="RSI (14)" value={ind.rsi.toFixed(1)} {...rsiInfo(ind.rsi)} />
                <IndicatorBox label="BOLL %B" value={ind.bollinger.percent_b.toFixed(2)} sub={`Width ${(ind.bollinger.width * 100).toFixed(1)}%`} />
                <IndicatorBox label="MACD HIST" value={ind.macd.histogram.toFixed(3)} sub={ind.macd.histogram > 0 ? "Bullish" : "Bearish"} highlight={ind.macd.histogram > 0 ? "green" : "red"} />
                <IndicatorBox label="VOLUME / 20D" value={`${ind.volume.ratio.toFixed(2)}x`} sub={volumeInfo(ind.volume.ratio)} />
                <IndicatorBox label="MA 50" value={ind.moving_averages.ma50.toFixed(2)} {...maInfo(ind.last_price, ind.moving_averages.ma50)} />
                <IndicatorBox label="MA 200" value={ind.moving_averages.ma200.toFixed(2)} {...maInfo(ind.last_price, ind.moving_averages.ma200)} />
                <IndicatorBox label="LONG %" value={`${ind.long_short.long_pct.toFixed(0)}%`} sub="Posizionamento" highlight="green" />
                <IndicatorBox label="SHORT %" value={`${ind.long_short.short_pct.toFixed(0)}%`} sub="Posizionamento" highlight="red" />
            </div>

            {/* Fibonacci */}
            <div className="border border-soft bg-elev mt-4 p-4">
                <div className="text-xs text-mute tracking-wider mb-3">RITRACCIAMENTI FIBONACCI (60 GIORNI)</div>
                <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                    {[
                        ["0%", ind.fibonacci.level_0],
                        ["23.6%", ind.fibonacci.level_236],
                        ["38.2%", ind.fibonacci.level_382],
                        ["50%", ind.fibonacci.level_500],
                        ["61.8%", ind.fibonacci.level_618],
                        ["78.6%", ind.fibonacci.level_786],
                        ["100%", ind.fibonacci.level_100],
                    ].map(([lbl, v]) => {
                        const dist = ((ind.last_price - v) / v) * 100;
                        return (
                            <div key={lbl} className="border border-soft bg-elev2 p-2">
                                <div className="text-[10px] text-mute">{lbl}</div>
                                <div className="text-sm tnum font-semibold">{v.toFixed(2)}</div>
                                <div className={`text-[10px] tnum ${Math.abs(dist) < 1 ? "text-amber" : "text-mute"}`}>{dist.toFixed(2)}%</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Metadata / About */}
            {asset.meta && (asset.meta.sector || asset.meta.market_cap || asset.meta.summary) && (
                <div data-testid="asset-meta" className="border border-soft bg-elev mt-4 p-4">
                    <div className="text-xs text-mute tracking-wider mb-3">SCHEDA ASSET</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3 text-xs">
                        {asset.meta.exchange && <MetaCell label="EXCHANGE" value={asset.meta.exchange} />}
                        {asset.meta.currency && <MetaCell label="VALUTA" value={asset.meta.currency} />}
                        {asset.meta.sector && <MetaCell label="SETTORE" value={asset.meta.sector} />}
                        {asset.meta.industry && <MetaCell label="INDUSTRIA" value={asset.meta.industry} />}
                        {asset.meta.market_cap && <MetaCell label="MARKET CAP" value={fmtCompact(asset.meta.market_cap)} />}
                        {asset.meta.isin && <MetaCell label="ISIN" value={asset.meta.isin} />}
                        {asset.meta.trailing_pe && <MetaCell label="P/E" value={asset.meta.trailing_pe.toFixed(2)} />}
                        {asset.meta.dividend_yield != null && (
                            <MetaCell label="DIV YIELD" value={`${(asset.meta.dividend_yield * 100).toFixed(2)}%`} />
                        )}
                        {asset.meta.beta && <MetaCell label="BETA" value={asset.meta.beta.toFixed(2)} />}
                        {asset.meta.fifty_two_week_high && (
                            <MetaCell label="52W HIGH" value={format(asset.meta.fifty_two_week_high)} />
                        )}
                        {asset.meta.fifty_two_week_low && (
                            <MetaCell label="52W LOW" value={format(asset.meta.fifty_two_week_low)} />
                        )}
                    </div>
                    {asset.meta.summary && (
                        <p className="text-xs text-dim leading-relaxed">{asset.meta.summary}</p>
                    )}
                    {asset.meta.website && (
                        <a
                            href={asset.meta.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-amber hover:underline mt-2"
                        >
                            Sito ufficiale <ExternalLink size={11} />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

const MetaCell = ({ label, value }) => (
    <div>
        <div className="text-[10px] text-mute tracking-widest">{label}</div>
        <div className="text-sm font-semibold truncate" title={value}>{value}</div>
    </div>
);

const fmtCompact = (n) => {
    if (n == null) return "—";
    if (n >= 1e12) return (n / 1e12).toFixed(2) + "T";
    if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
    if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
    return n.toLocaleString();
};

const rsiInfo = (v) => {
    if (v < 30) return { sub: "Oversold", highlight: "green" };
    if (v > 70) return { sub: "Overbought", highlight: "red" };
    return { sub: "Neutral", highlight: "neutral" };
};

const volumeInfo = (ratio) => {
    if (ratio > 1.5) return "Elevato";
    if (ratio < 0.5) return "Debole";
    return "Normale";
};

const maInfo = (price, ma) => {
    const above = price > ma;
    return { sub: above ? "Sopra" : "Sotto", highlight: above ? "green" : "red" };
};

const IndicatorBox = ({ label, value, sub, highlight }) => {
    let color;
    if (highlight === "green") color = "var(--green)";
    else if (highlight === "red") color = "var(--red)";
    return (
        <div className="border border-soft bg-elev p-3">
            <div className="text-[10px] text-mute tracking-widest">{label}</div>
            <div className="text-xl font-bold tnum mt-1" style={color ? { color } : undefined}>{value}</div>
            <div className="text-[10px] text-dim mt-0.5">{sub}</div>
        </div>
    );
};
